import type { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import React from "react";
import type { TemplateQuoteDataGrid_ItemFragment } from "../../../main-routes/templates/quote/TemplateQuoteDataGrid.generated.js";
import type { DocDataGridRow } from "../../doc-data-grid/types.js";
import { useDocColumns } from "../../doc-data-grid/useDocColumns.js";

interface Options {
  docId: string;
  canEdit: boolean;
  itemCanBeEdited?: (item: TemplateQuoteDataGrid_ItemFragment) => boolean;
  expandedItemIds: string[] | undefined;
  viewerIsContractor: boolean;
}

export function useTemplateColumns(options: Options): {
  columns: GridColDef<DocDataGridRow<TemplateQuoteDataGrid_ItemFragment>>[];
  initialState: GridInitialState;
} {
  const {
    contingencyCheckboxColumn,
    decisionOptionColumn,
    decisionBehaviorColumn,
    descriptionColumn,
    productColumns,
    calculationColumns,
    initialState,
  } = useDocColumns<TemplateQuoteDataGrid_ItemFragment>({
    // Templates have no project.
    projectId: null,
    docId: options.docId,
    canEdit: options.canEdit,
    itemCanBeEdited: options.itemCanBeEdited,
    expandedItemIds: options.expandedItemIds,
    viewerIsContractor: options.viewerIsContractor,
  });

  // No template placeholder / wizard-preview columns (quote-only). The three
  // decision columns sit contiguously since nothing is interleaved.
  const columns = React.useMemo(
    (): GridColDef<DocDataGridRow<TemplateQuoteDataGrid_ItemFragment>>[] => [
      contingencyCheckboxColumn,
      decisionOptionColumn,
      decisionBehaviorColumn,
      descriptionColumn,
      ...productColumns,
      ...calculationColumns,
    ],
    [
      contingencyCheckboxColumn,
      decisionOptionColumn,
      decisionBehaviorColumn,
      descriptionColumn,
      productColumns,
      calculationColumns,
    ]
  );

  return { columns, initialState };
}
